import { useContext, useState } from "react";
import { HiCheckCircle } from "react-icons/hi";
import InputModern from "components/ui/Input/InputModern";
import useConfigPanel from "utils/hooks/useConfigPanel";
import { CalendarActiveSocialNetworkContext } from "contexts/CalendarActiveSocialNetworkContext";
import ConfigPanelPostTypes from "./ConfigPanelPostTypes";
import ConfigPanelImageTypes from "./ConfigPanelImageTypes";
import { HiOutlineChevronDown, HiOutlineChevronUp } from "react-icons/hi2";

const { Button, Dropdown } = require("components/ui");
const { Trans } = require("react-i18next");

const ConfigPanel = ({
    selected,
    onSubmit,
    config,
    setConfig,
    template = "",
}) => {

    const { tones, formats, ctas, handleOnNewCta, handleOnNewTone, handleOnNewFormat } = useConfigPanel(template, config?.postType);
    const [ advancedOptionsVisible, setAdvancedOptionsVisible ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);

    return (
        <div id="configPanel" className='!z-[10000] lg:!z-auto !fixed lg:!sticky !bottom-0 lg:!bottom-auto !left-0 lg:!left-auto !top-auto lg:!top-4 sticky !w-full !h-fit lg:!h-auto lg:!w-[300px] bg-secondary-500 dark:bg-gray-800 flex flex-col justify-between lg:gap-2 rounded-t-2xl lg:rounded-2xl !overflow-y-visible border-t border-tertiary-100 dark:border-gray-700 lg:border-t-none dark:lg:!border-transparent'>
            <div className={`lg:p-4 pb-0 flex-1 flex flex-row lg:flex-col lg:gap-5 ${!!advancedOptionsVisible ? `max-h-[300px] overflow-y-auto` : `overflow-y-visible`}`}>

                <div className="flex flex-col gap-1 w-full">
                    <label className="font-semibold px-2 hidden lg:inline-block"><Trans i18nKey={`askAITemplate.postType`}>Post Type</Trans></label>
                    <ConfigPanelPostTypes 
                        postType={config?.postType}
                        setPostType={(postType) => setConfig({...config, postType: postType})}
                        selectedPrompts={config?.prompts}
                        onSelectPrompt={(prompt) => {
                            let prompts = config?.prompts || [];
                            if (prompts?.find(e => e.id == prompt.id)) {
                                prompts = prompts.filter(e => e.id != prompt.id);
                            } else {
                                prompts = [...prompts, prompt];
                            }
                            setConfig({...config, prompts: prompts})
                        }}
                        template={template}
                        className="!rounded-none lg:!rounded-2xl bg-secondary-500 lg:bg-gray-50 justify-center lg:justify-start !px-0"
                        placement={window.innerWidth < 1024 ? "top-start" : undefined}
                    />
                </div>

                {config?.postType != "tiktok" && <div className="flex flex-col gap-1 w-full">
                    <label className="font-semibold px-2 hidden lg:inline-block"><Trans i18nKey={`askAITemplate.imageType`}>Image Type</Trans></label>
                    <ConfigPanelImageTypes 
                        config={config}
                        setConfig={setConfig}
                        template={template}
                        className="!rounded-none lg:!rounded-2xl bg-secondary-500 lg:bg-gray-50 justify-center lg:justify-start !px-0 lg:"
                        placement={window.innerWidth < 1024 ? "top-end" : undefined}
                    />
                </div>}

                {!!advancedOptionsVisible && <>
                    <div className="flex flex-col gap-1 w-full">
                        <label className="font-semibold px-2"><Trans i18nKey={`askAITemplate.chooseTone`}>Choose a tone</Trans></label>
                        <Dropdown
                            menuClass="max-h-[250px] w-[240px] overflow-y-auto !rounded-2xl !border-0 !p-0"
                            placement="bottom-start"
                            renderTitle={
                                <div className="cursor-pointer !pl-5 overflow-hidden whitespace-nowrap !px-10 !py-3 !h-auto bg-gray-50 dark:bg-gray-600 rounded-2xl items-center flex gap-1 font-semibold" >
                                    {tones?.filter(e => e.value == config?.tone)?.[0]?.emoji ? <span className="text-2xl !leading-[0px]">{tones?.filter(e => e.value == config?.tone)?.[0]?.emoji}&nbsp;</span> : ``} {config?.tone ? config?.tone : "Default"}
                                </div>
                            }
                        >
                            <Dropdown.Item className="!px-0">
                                <div className="flex justify-between items-center gap-2 !px-2 !py-2 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                    <InputModern 
                                        className="!px-4" 
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Write a custom tone" 
                                        onKeyDown={(e) => {
                                            if (e.key == 'Enter') {
                                                handleOnNewTone(e.target.value); 
                                                setConfig({...config, tone: e.target.value});
                                                e.target.value = "";
                                            } 
                                        }}
                                    />
                                </div>
                            </Dropdown.Item>
                            <Dropdown.Item className="!px-0">
                                <div onClick={() => setConfig({...config, tone: ""})} className="flex justify-between items-center gap-2 !px-4 !py-2 cursor-pointer dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                    <div className='flex items-center gap-2'>
                                        <span><Trans i18nKey={`askAITemplate.default`}>Default</Trans></span>
                                    </div>
                                    <HiCheckCircle className={`text-xl text-primary-600 shrink-0 ${"" != config?.tone && `opacity-[0.1]`}`} />
                                </div>
                            </Dropdown.Item>
                            {tones?.map((_tone, key) => (
                                <Dropdown.Item className="!px-0">
                                    <div onClick={() => setConfig({...config, tone: _tone?.value })} key={key} className="flex justify-between items-center gap-2 !px-4 !py-2 cursor-pointer dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                        <div className='flex items-center gap-2'>
                                            <span>{_tone?.emoji}&nbsp; {_tone?.name}</span>
                                        </div>
                                        <HiCheckCircle className={`text-xl text-primary-600 shrink-0 ${_tone?.value != config?.tone && `opacity-[0.1]`}`} />
                                    </div>
                                </Dropdown.Item>
                            ))}
                        </Dropdown>
                    </div>

                    <div className="flex flex-col gap-1 w-full">
                        <label className="font-semibold px-2"><Trans i18nKey={`askAITemplate.chooseFormat`}>Choose a format</Trans></label>
                        <Dropdown
                            menuClass="max-h-[250px] w-[240px] overflow-y-auto !rounded-2xl !border-0 !p-0"
                            placement="bottom-start"
                            renderTitle={
                                <div className="cursor-pointer !pl-5 overflow-hidden whitespace-nowrap !px-10 !py-3 !h-auto bg-gray-50 dark:bg-gray-600 rounded-2xl items-center flex gap-1 font-semibold" >
                                    {formats?.filter(e => e.value == config?.cta)?.[0]?.emoji ? <span className="text-2xl !leading-[0px]">{formats?.filter(e => e.value == config?.cta)?.[0]?.emoji}&nbsp;</span> : ``} {config?.format ? config?.format : "Default"}
                                </div>
                            }
                        >
                            <Dropdown.Item className="!px-0">
                                <div className="flex justify-between items-center gap-2 !px-2 !py-2 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                    <InputModern 
                                        className="!px-4" 
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Write a custom format" 
                                        onKeyDown={(e) => {
                                            if (e.key == 'Enter') {
                                                handleOnNewFormat(e.target.value); 
                                                setConfig({...config, format: e.target.value});
                                                e.target.value = "";
                                            } 
                                        }}
                                    />
                                </div>
                            </Dropdown.Item>
                            <Dropdown.Item className="!px-0">
                                <div onClick={() => setConfig({...config, format: ""})} className="flex justify-between items-center gap-2 !px-4 !py-2 cursor-pointer dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                    <div className='flex items-center gap-2'>
                                        <span><Trans i18nKey={`askAITemplate.default`}>Default</Trans></span>
                                    </div>
                                    <HiCheckCircle className={`text-xl text-primary-600 shrink-0 ${"" != config?.format && `opacity-[0.1]`}`} />
                                </div>
                            </Dropdown.Item>
                            {formats?.map((_format, key) => (
                                <Dropdown.Item className="!px-0">
                                    <div onClick={() => setConfig({...config, format: _format?.value })} key={key} className="flex justify-between items-center gap-2 !px-4 !py-2 cursor-pointer dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                        <div className='flex items-center gap-2'>
                                            <span>{_format?.name}</span>
                                        </div>
                                        <HiCheckCircle className={`text-xl text-primary-600 shrink-0 ${_format?.value != config?.format && `opacity-[0.1]`}`} />
                                    </div>
                                </Dropdown.Item>
                            ))}
                        </Dropdown>
                    </div>
                    <div className="flex flex-col gap-1 w-full">
                        <label className="font-semibold px-2"><Trans i18nKey={`askAITemplate.chooseCTA`}>Choose a Call To Action</Trans></label>
                        <Dropdown
                            menuClass="max-h-[250px] w-[240px] overflow-y-auto !rounded-2xl !border-0 !p-0"
                            placement="bottom-start"
                            renderTitle={
                                <div className="cursor-pointer !pl-5 overflow-hidden whitespace-nowrap !px-10 !py-3 !h-auto bg-gray-50 dark:bg-gray-600 rounded-2xl items-center flex gap-1 font-semibold" >
                                    {ctas?.filter(e => e.value == config?.cta)?.[0]?.emoji ? <span className="text-2xl !leading-[0px]">{ctas?.filter(e => e.value == config?.cta)?.[0]?.emoji}&nbsp;</span> : ``} {config?.cta ? config?.cta : "None"}
                                </div>
                            }
                        >
                            <Dropdown.Item className="!px-0">
                                <div className="flex justify-between items-center gap-2 !px-2 !py-2 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                    <InputModern 
                                        className="!px-4" 
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Write a custom CTA" 
                                        onKeyDown={(e) => {
                                            if (e.key == 'Enter') {
                                                handleOnNewCta(e.target.value); 
                                                setConfig({...config, cta: e.target.value});
                                                e.target.value = "";
                                            } 
                                        }}
                                    />
                                </div>
                            </Dropdown.Item>
                            <Dropdown.Item className="!px-0">
                                <div onClick={() => setConfig({...config, cta: ""})} className="flex justify-between items-center gap-2 !px-4 !py-2 cursor-pointer dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                    <div className='flex items-center gap-2'>
                                        <span><Trans i18nKey={`askAITemplate.none`}>None</Trans></span>
                                    </div>
                                    <HiCheckCircle className={`text-xl text-primary-600 shrink-0 ${"" != config?.cta && `opacity-[0.1]`}`} />
                                </div>
                            </Dropdown.Item>
                            {ctas?.map((_cta, key) => (
                                <Dropdown.Item className="!px-0">
                                    <div onClick={() => setConfig({...config, cta: _cta?.value })} key={key} className="flex justify-between items-center gap-2 !px-4 !py-2 cursor-pointer dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 w-full">
                                        <div className='flex items-center gap-2'>
                                            <span>{_cta?.emoji}&nbsp; {_cta?.name}</span>
                                        </div>
                                        <HiCheckCircle className={`text-xl text-primary-600 shrink-0 ${_cta?.value != config?.cta && `opacity-[0.1]`}`} />
                                    </div>
                                </Dropdown.Item>
                            ))}
                        </Dropdown>
                    </div>

                    <div className="flex flex-col gap-1 w-full">
                        <label className="font-semibold px-2"><Trans i18nKey={`askAITemplate.extraInstructions`}>Extra Instructions</Trans></label>
                        <InputModern
                            value={config?.extraInstructions}
                            onChange={(e) => setConfig({...config, extraInstructions: e.target.value })} 
                            placeholder={` - Make the post about...
 - Do not use emojis
 - Use only two hashtags`}
                            textArea
                            className="!min-h-0 !bg-gray-50 dark:!bg-gray-600 !rounded-2xl"
                            fieldsetClassName="!h-[83px] !min-h-0"
                        />
                    </div>
                </>}
            </div>
            <div onClick={() => setAdvancedOptionsVisible(!advancedOptionsVisible)} className="w-full text-center cursor-pointer transition-all hover:opacity-80 hidden lg:flex items-center justify-center gap-2">
                {advancedOptionsVisible 
                    ? <>
                        <span>Hide advanced options</span>
                        <HiOutlineChevronUp />
                    </> : <>
                        <span>Show advanced options</span>
                        <HiOutlineChevronDown />
                    </>
                }
            </div>
            <div className='shrink-0 lg:p-4 pt-0 w-full'>
                <Button 
                    loading={isLoading} 
                    className="w-full flex items-center gap-1.5 justify-center !rounded-none lg:!rounded-2xl" 
                    onClick={() => { onSubmit(); setIsLoading(true); }} 
                    disabled={!selected?.length} 
                    variant="solid"
                >
                    <img src={`/img/socials/${config?.postType}.svg`} className="h-4 w-4 inline" />
                    <div className="hidden lg:inline">
                        <Trans i18nKey={`imagesTemplate.generate`}>Generate </Trans> {selected?.length} <Trans i18nKey={`imageTemplate.images`}>posts</Trans>
                    </div>
                    <div className="inline lg:hidden">
                        <Trans i18nKey={`imagesTemplate.generate`}>Generate</Trans>
                    </div>
                </Button>
            </div>
        </div>
    );
}

export default ConfigPanel;